import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { isLoaded } from "react-redux-firebase";
import get from "lodash.get";

const filterEmptyFields = (data: any): any => {
  if (!data || typeof data !== "object") {
    return data;
  }

  const filteredData: any = {};

  Object.keys(data).forEach(key => {
    const value = data[key];

    if (value === null || value === undefined) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (
      typeof value === "object" &&
      value !== null &&
      Object.keys(value).length === 0
    )
      return;

    filteredData[key] = value;
  });

  return filteredData;
};

interface ProgressData {
  currentSubStep: number;
  totalSubSteps: number;
  formData: any;
  status: 'draft' | 'submitted' | 'completed';
  lastSaved: number;
}

interface UseOrganizationProgressProps {
  charityId: string | null;
  firebase: any;
}

interface UseOrganizationProgressReturn {
  isLoading: boolean;
  isReady: boolean;
  error: string | null;
  saveDraft: (
    data: any,
    currentStep: number,
    totalSteps: number
  ) => Promise<void>;
  markAsSubmitted: () => Promise<void>;
  clearDraft: () => Promise<void>;
}

export const useOrganizationProgress = ({
  charityId,
  firebase,
}: UseOrganizationProgressProps): UseOrganizationProgressReturn => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsReady(true);
    setIsLoading(false);
  }, []);

  const saveDraft = async (
    data: any,
    currentStep: number,
    totalSteps: number
  ): Promise<void> => {
    if (!charityId || !firebase) {
      setError("Missing charity ID or Firebase connection");
      return;
    }

    const filteredData = filterEmptyFields(data);

    const progressData: ProgressData = {
      currentSubStep: currentStep,
      totalSubSteps: totalSteps,
      formData: filteredData,
      status: 'draft',
      lastSaved: Date.now(),
    };

    try {
      await firebase
        .ref(`${charityId}/charitySetup/organization`)
        .set(progressData);
      setError(null);
    } catch (error) {
      console.error("Error saving draft:", error);
      setError("Failed to save draft");
      throw error;
    }
  };

  const markAsSubmitted = async (): Promise<void> => {
    if (!charityId || !firebase) {
      setError("Missing charity ID or Firebase connection");
      return;
    }

    try {
      await firebase
        .ref(`${charityId}/charitySetup/organization`)
        .update({
          status: 'submitted',
          lastSaved: Date.now(),
        });
      setError(null);
    } catch (error) {
      console.error("Error marking as submitted:", error);
      setError("Failed to mark as submitted");
      throw error;
    }
  };

  const clearDraft = async (): Promise<void> => {
    if (!charityId || !firebase) {
      setError("Missing charity ID or Firebase connection");
      return;
    }

    try {
      await firebase
        .ref(`${charityId}/charitySetup/organization`)
        .set({
          status: 'completed',
          completedAt: Date.now(),
        });
      setError(null);
    } catch (error) {
      console.error("Error clearing draft:", error);
      setError("Failed to clear draft");
      throw error;
    }
  };

  return {
    isLoading,
    isReady,
    error,
    saveDraft,
    markAsSubmitted,
    clearDraft,
  };
};

export const getOrganizationProgressFromState = (state: any) => {
  const { user, firebase } = state;
  const charityId = user.currentCharity?.id || null;

  const draftLoaded = charityId
    ? isLoaded(
        firebase.data[charityId]?.charitySetup?.organization
      )
    : true;

  const firebaseDraft =
    draftLoaded && charityId
      ? get(
          firebase,
          `data.${charityId}.charitySetup.organization`,
          null
        )
      : null;

  return {
    charityId,
    firebaseDraft,
    draftLoaded,
    firebase: firebase,
  };
};
